import { Service, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ClientCompanyDataService } from '@data-services/companies';
import { Company } from '@models/companies';
import { DataContext } from '@services/data-context';
import { createPageResource, resolveMappedName, toNameMap } from '@utils';

interface ProjectParams {
  projectId: string;
}

@Service({ autoProvided: false })
export class ClientCompaniesService {
  private readonly companyDataService = inject(ClientCompanyDataService);
  private readonly dataContext = inject(DataContext);
  private readonly translate = inject(TranslateService);

  private readonly companiesRequested = signal(false);
  private readonly namesRequested = signal(false);

  private projectParams(requested: boolean): ProjectParams | undefined {
    const projectId = this.dataContext.projectContext()?.id;
    return requested && projectId ? { projectId } : undefined;
  }

  readonly resource = createPageResource<Company[], ProjectParams>({
    params: () => this.projectParams(this.companiesRequested()),
    loadAsync: ({ projectId }) => this.companyDataService.getCompaniesAsync(projectId),
    errorMessage: () => this.translate.instant('general.loadCompaniesError'),
    fallbackValue: []
  });

  readonly namesResource = createPageResource<Map<string, string>, ProjectParams>({
    params: () => this.projectParams(this.namesRequested()),
    loadAsync: async ({ projectId }) => toNameMap(await this.companyDataService.getCompanyNameMapAsync(projectId)),
    errorMessage: () => this.translate.instant('general.loadCompaniesError'),
    fallbackValue: new Map()
  });

  requestCompanies(): void {
    this.companiesRequested.set(true);
  }

  requestNames(): void {
    this.namesRequested.set(true);
  }

  companyName(companyId: string | undefined): string {
    return resolveMappedName(this.namesResource.value(), companyId);
  }
}
